/**
 * 도막 측정기 사진 OCR 컴포넌트
 *
 * 사용법:
 * 1. 부위 선택 (본넷, 루프, 좌펜더 등)
 * 2. 도막 측정기 화면 사진 촬영 또는 갤러리 선택
 * 3. Claude Vision API로 수치 자동 추출
 * 4. 해당 부위에 수치 자동 입력
 *
 * Props:
 *   paintData: { hood: '', roof: '', ... } - 현재 도막 데이터
 *   onUpdate: (key, value) => void - 수치 업데이트 콜백
 *   apiBase: string - 서버 API URL (기본: http://localhost:4000)
 */
import React, { useState, useRef } from 'react';
import { API } from '../utils/api';

const PAINT_PARTS = [
  { key: 'hood',    label: '본넷',   icon: '▤' },
  { key: 'roof',    label: '루프',   icon: '▥' },
  { key: 'fenderL', label: '좌펜더', icon: '◧' },
  { key: 'fenderR', label: '우펜더', icon: '◨' },
  { key: 'doorFL',  label: '좌앞문', icon: '◧' },
  { key: 'doorFR',  label: '우앞문', icon: '◨' },
  { key: 'doorRL',  label: '좌뒷문', icon: '◧' },
  { key: 'doorRR',  label: '우뒷문', icon: '◨' },
  { key: 'trunk',    label: '트렁크',   icon: '▦' },
  { key: 'quarterL', label: '좌뒤펜더', icon: '◧' },
  { key: 'quarterR', label: '우뒤펜더', icon: '◨' },
];

const getPaintColor = (v) => {
  if (!v) return '#ccc';
  var n = typeof v === 'string' ? parseInt(v, 10) : v;
  if (isNaN(n)) return '#ccc';
  if (n < 150) return '#1D9E75';
  if (n < 250) return '#EF9F27';
  return '#E24B4A';
};

export default function PaintOCR({ paintData = {}, onUpdate, apiBase }) {
  var [selectedPart, setSelectedPart] = useState(null);
  var [loading, setLoading] = useState(false);
  var [preview, setPreview] = useState(null);
  var [ocrResult, setOcrResult] = useState(null);
  var [error, setError] = useState(null);
  var [history, setHistory] = useState([]);
  var fileRef = useRef(null);
  var base = apiBase || API.replace('/api', '');

  // 부위 선택 → 카메라 열기
  var handlePartSelect = function(part) {
    setSelectedPart(part);
    setPreview(null);
    setOcrResult(null);
    setError(null);
    setTimeout(function() { if (fileRef.current) fileRef.current.click(); }, 100);
  };

  // 사진 선택/촬영
  var handleFileChange = async function(e) {
    var file = e.target.files?.[0];
    if (!file || !selectedPart) return;

    // 미리보기
    var reader = new FileReader();
    reader.onload = function(ev) { setPreview(ev.target.result); };
    reader.readAsDataURL(file);

    // OCR 요청
    setLoading(true);
    setError(null);
    setOcrResult(null);

    try {
      var formData = new FormData();
      formData.append('image', file);
      formData.append('part', selectedPart.key);
      formData.append('partLabel', selectedPart.label);

      var res = await fetch(base + '/api/eval/paint-ocr', {
        method: 'POST',
        body: formData,
      });

      var data = await res.json();

      if (data.success && data.value) {
        var numValue = parseFloat(data.value);
        setOcrResult({
          value: numValue,
          raw: data.raw || '',
          confidence: data.confidence || 'high',
        });
        if (onUpdate) onUpdate(selectedPart.key, String(numValue));
        setHistory(function(prev) { return [{
          part: selectedPart,
          value: numValue,
          timestamp: new Date().toLocaleTimeString(),
          preview: preview,
        }].concat(prev); });
      } else {
        setError(data.error || '수치를 읽을 수 없습니다. 다시 촬영해주세요.');
      }
    } catch (err) {
      setError('서버 연결 실패. 수동으로 입력해주세요.');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // 수동 재시도
  var handleRetry = function() {
    setPreview(null);
    setOcrResult(null);
    setError(null);
    if (fileRef.current) fileRef.current.click();
  };

  return (
    <div>
      {/* 숨겨진 파일 input (카메라 캡처 우선) */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* 부위 선택 그리드 */}
      <div style={{ fontSize: 13, fontWeight: 600, color: '#534AB7', marginBottom: 8 }}>
        도막 사진 촬영 (부위 선택 → 촬영)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 }}>
        {PAINT_PARTS.map(function(part) {
          var val = paintData[part.key];
          var hasValue = val && val !== '';
          var color = getPaintColor(val);
          var isSelected = selectedPart?.key === part.key;
          return (
            <button
              key={part.key}
              onClick={function() { handlePartSelect(part); }}
              disabled={loading}
              style={{
                padding: '10px 6px', borderRadius: 10, cursor: loading ? 'default' : 'pointer',
                border: isSelected ? '2px solid #534AB7' : hasValue ? '1.5px solid ' + color : '1px solid #e0e0da',
                background: hasValue ? color + '11' : '#fff',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                WebkitTapHighlightColor: 'transparent',
                opacity: loading && !isSelected ? 0.5 : 1,
              }}
            >
              <span style={{ fontSize: 11, color: hasValue ? color : '#888', fontWeight: 600 }}>
                {part.label}
              </span>
              {hasValue ? (
                <span style={{ fontSize: 16, fontWeight: 800, color: color }}>
                  {val}
                </span>
              ) : (
                <span style={{ fontSize: 18, color: '#ccc' }}>📷</span>
              )}
            </button>
          );
        })}
      </div>

      {/* 촬영 중 / 결과 표시 */}
      {selectedPart && (loading || preview) && (
        <div style={{
          background: '#fff', borderRadius: 14, border: '2px solid #534AB7',
          padding: 14, marginBottom: 12,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#222', marginBottom: 10 }}>
            {selectedPart.label} 도막 측정
          </div>

          {/* 로딩 */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 36, height: 36, border: '3px solid #eee',
                borderTopColor: '#534AB7', borderRadius: '50%',
                margin: '0 auto 12px',
                animation: 'spin 0.8s linear infinite',
              }} />
              <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
              <div style={{ fontSize: 14, color: '#534AB7', fontWeight: 600 }}>수치 분석중...</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Claude Vision이 도막 측정기를 읽고 있어요</div>
            </div>
          )}

          {/* 미리보기 + 결과 */}
          {!loading && preview && (
            <div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <img src={preview} alt="도막 측정" style={{
                  width: 100, height: 100, objectFit: 'cover',
                  borderRadius: 10, border: '1px solid #eee',
                }} />
                <div style={{ flex: 1 }}>
                  {ocrResult && (
                    <div>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>인식된 수치</div>
                      <div style={{
                        fontSize: 32, fontWeight: 800,
                        color: getPaintColor(ocrResult.value),
                      }}>
                        {Number.isInteger(ocrResult.value) ? ocrResult.value : ocrResult.value.toFixed(1)}<span style={{ fontSize: 14, fontWeight: 400, color: '#888' }}> μm</span>
                      </div>
                      <div style={{
                        display: 'inline-block', marginTop: 4,
                        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        color: ocrResult.value < 150 ? '#1D9E75' : ocrResult.value < 250 ? '#BA7517' : '#A32D2D',
                        background: ocrResult.value < 150 ? '#E1F5EE' : ocrResult.value < 250 ? '#FAEEDA' : '#FCEBEB',
                      }}>
                        {ocrResult.value < 150 ? '정상' : ocrResult.value < 250 ? '도장 의심' : '판금/용접 의심'}
                      </div>
                    </div>
                  )}
                  {error && (
                    <div style={{ fontSize: 13, color: '#E24B4A', fontWeight: 600, marginBottom: 8 }}>{error}</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleRetry} style={{
                  flex: 1, padding: '10px 0', borderRadius: 8,
                  border: '1px solid #ddd', background: '#f8f8f5',
                  cursor: 'pointer', fontSize: 13, color: '#666',
                }}>다시 촬영</button>
                <button onClick={function() { setSelectedPart(null); setPreview(null); setOcrResult(null); setError(null); }} style={{
                  flex: 1, padding: '10px 0', borderRadius: 8,
                  border: 'none', background: '#534AB7',
                  cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600,
                }}>확인</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 촬영 히스토리 */}
      {history.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>촬영 기록</div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            {history.map(function(h, i) {
              return (
                <div key={i} style={{
                  minWidth: 70, textAlign: 'center', padding: '6px 8px',
                  background: '#f8f8f5', borderRadius: 8, flexShrink: 0,
                }}>
                  <div style={{ fontSize: 10, color: '#aaa' }}>{h.part.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: getPaintColor(h.value) }}>{Number.isInteger(h.value) ? h.value : h.value.toFixed(1)}</div>
                  <div style={{ fontSize: 9, color: '#ccc' }}>{h.timestamp}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 안내 */}
      <div style={{
        background: '#f8f8f5', borderRadius: 10, padding: '10px 14px',
        fontSize: 12, color: '#888', lineHeight: 1.5,
      }}>
        <strong style={{ color: '#666' }}>촬영 팁:</strong> 도막 측정기 화면의 숫자가 선명하게 보이도록 촬영해주세요.
        인식이 안 되면 수동으로 입력할 수 있어요.
      </div>
    </div>
  );
}
